// =============================================================================
// COUTS BREAKDOWN - Donut chart animé premium
// =============================================================================

import { useState, useMemo, useRef, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import { Wallet, Info } from 'lucide-react';
import { CoutItem } from '@/hooks/useChartData';
import {
  formatPourcentage,
  CHART_STATUS_COLORS,
  TOOLTIP_EXPLANATIONS,
} from './chartConfig';

interface Props {
  data: CoutItem[];
}

// =============================================================================
// SEGMENT ACTIF (HOVER) - Version simplifiée sans bug
// =============================================================================

const renderActiveShape = (props: any) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
  } = props;

  return (
    <g>
      {/* Segment principal légèrement agrandi */}
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 2}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function CoutsBreakdown({ data }: Props) {
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(undefined);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Total des coûts
  const totalCouts = useMemo(() => {
    return data.reduce((sum, item) => sum + item.value, 0);
  }, [data]);

  // L'élément actif pour le graphique (hover OU sélectionné)
  const activeIndex = hoveredIndex ?? selectedIndex;
  const activeItem = activeIndex !== undefined ? data[activeIndex] : null;

  // L'élément sélectionné pour l'info-bulle (uniquement au clic)
  const selectedItem = selectedIndex !== undefined ? data[selectedIndex] : null;

  // Gestion hover stabilisée
  const handleEnter = useCallback((index: number) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setHoveredIndex(index);
  }, []);

  const handleLeave = useCallback(() => {
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(undefined);
    }, 100);
  }, []);

  // Gestion clic pour afficher l'info-bulle
  const handleClick = useCallback((index: number) => {
    setSelectedIndex(prev => prev === index ? undefined : index);
  }, []);

  const onPieEnter = (_: any, index: number) => {
    handleEnter(index);
  };

  const onPieLeave = () => {
    handleLeave();
  };

  // Explication selon la catégorie
  const getExplanation = (name: string): string => {
    switch (name) {
      case "Main d'œuvre":
        return TOOLTIP_EXPLANATIONS.mainOeuvre;
      case 'Fournisseurs':
        return 'Factures des fournisseurs de matériaux et équipements.';
      case 'Location de véhicule':
        return 'Location de véhicules pour les déplacements et transports sur chantier.';
      case "Achat d'outillage":
        return 'Achat d\'outils et équipements pour les travaux.';
      case "Location d'outillage":
        return 'Location d\'outils et matériel spécialisé.';
      case 'Consommables':
        return 'Petit matériel et consommables utilisés sur le chantier.';
      case 'Frais de déplacement':
        return 'Déplacements, carburant et frais kilométriques.';
      case "Frais d'hébergement":
        return 'Hébergement des équipes sur chantier.';
      case 'Assurance':
        return 'Assurances chantier et garanties.';
      case 'Permis & autorisations':
        return 'Permis de construire, autorisations administratives.';
      case 'Sous-traitant':
        return TOOLTIP_EXPLANATIONS.sousTraitance;
      case 'Autre':
        return 'Autres frais divers.';
      default:
        return `Frais de type "${name}".`;
    }
  };

  if (!data.length || totalCouts === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-6">
        <Wallet className="h-16 w-16 mb-6 opacity-30" />
        <p className="text-base font-medium mb-2">Pas de données de coûts</p>
        <p className="text-sm opacity-70 text-center mb-6">
          Les coûts apparaîtront après les premières dépenses
        </p>

        {/* Aperçu des catégories de coûts */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: `${CHART_STATUS_COLORS.mainOeuvre}40` }}
            />
            <span className="text-muted-foreground">Main d'œuvre</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: `${CHART_STATUS_COLORS.materiaux}40` }}
            />
            <span className="text-muted-foreground">Matériaux</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: `${CHART_STATUS_COLORS.sousTraitance}40` }}
            />
            <span className="text-muted-foreground">Sous-traitance</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: `${CHART_STATUS_COLORS.frais}40` }}
            />
            <span className="text-muted-foreground">Frais</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row items-center justify-center gap-6">
      {/* Donut Chart */}
      <div className="relative w-full max-w-[260px] aspect-square flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart style={{ outline: 'none' }} className="[&_path]:outline-none [&_*]:outline-none">
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={3}
              dataKey="value"
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              onClick={(_: any, index: number) => handleClick(index)}
              animationDuration={800}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke="transparent"
                  style={{ cursor: 'pointer', outline: 'none' }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Centre du donut - Total ou détail */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {activeItem ? (
            <>
              <span
                className="text-2xl font-bold"
                style={{ color: activeItem.color }}
              >
                {formatPourcentage(activeItem.pct)}
              </span>
              <span className="text-sm text-muted-foreground mt-1">{activeItem.name}</span>
            </>
          ) : (
            <>
              <span className="text-2xl font-bold text-foreground">
                {Math.round(totalCouts).toLocaleString('fr-FR')} €
              </span>
              <span className="text-sm text-muted-foreground mt-1">Total coûts</span>
            </>
          )}
        </div>
      </div>

      {/* Légende et détails - scrollable si beaucoup d'éléments */}
      <div className="flex flex-col gap-2 min-w-[220px] max-w-[280px] max-h-[350px] overflow-y-auto pr-1">
        {data.map((item, index) => (
          <div
            key={item.name}
            className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer
              transition-colors duration-200 ease-out
              ${activeIndex === index
                ? 'bg-card border border-border shadow-md'
                : 'bg-card/20 border border-transparent hover:bg-card/40'
              }
              ${selectedIndex === index ? 'ring-2 ring-primary/50' : ''}`}
            onMouseEnter={() => handleEnter(index)}
            onMouseLeave={handleLeave}
            onClick={() => handleClick(index)}
          >
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground truncate">
                  {item.name}
                </span>
                <span className="text-sm font-bold whitespace-nowrap" style={{ color: item.color }}>
                  {Math.round(item.value).toLocaleString('fr-FR')} €
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatPourcentage(item.pct)} du total
              </span>
            </div>
          </div>
        ))}

        {/* Info bulle détail - uniquement au clic */}
        {selectedItem && (
          <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
            <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">{selectedItem.name}</span>
              <br />
              {getExplanation(selectedItem.name)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
